import React, { useRef, useState } from "react";
import TalkingFace from "./TalkingFace";

/* A scratch page for judging the animation on its own, before any of it goes
   near the product. Left is the reference clip, right is the rebuilt face. */

const MODES = [
  { id: "captured", label: "Captured", hint: "replays the clip's own curves" },
  { id: "procedural", label: "Procedural", hint: "generates its own speech, endlessly" },
];

const SPEEDS = [0.5, 0.75, 1, 1.5];

function Control({ children }) {
  return <div className="flex flex-wrap items-center gap-2">{children}</div>;
}

function Pill({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="rounded-full px-4 py-2 text-sm font-medium transition-colors"
      style={{
        background: active ? "#5fe9f5" : "rgba(255,255,255,.06)",
        color: active ? "#04191c" : "rgba(255,255,255,.72)",
        border: "1px solid rgba(255,255,255,.12)",
      }}
    >
      {children}
    </button>
  );
}

export default function TalkingFaceLab() {
  const [mode, setMode] = useState("captured");
  const [speed, setSpeed] = useState(1);
  const [playing, setPlaying] = useState(true);
  const [glow, setGlow] = useState(5.5);
  const [color, setColor] = useState("#5fe9f5");
  const [showRef, setShowRef] = useState(true);
  const videoRef = useRef(null);

  return (
    <main style={{ background: "#06090b", minHeight: "100vh", color: "#e9f6f8" }}>
      <div className="mx-auto w-full max-w-[1200px] px-6 py-10">
        <h1 className="text-2xl font-semibold">Talking face</h1>
        <p className="mt-2 text-sm" style={{ color: "rgba(233,246,248,.55)" }}>
          Rebuilt from the reference clip as continuous parameters. 240 captured frames
          at 24 fps, resampled every animation frame.
        </p>

        <div className="mt-8 grid gap-6 lg:grid-cols-2">
          {showRef && (
            <figure className="rounded-3xl p-4" style={{ background: "#000", border: "1px solid rgba(255,255,255,.1)" }}>
              <video
                ref={videoRef}
                src="/lab/reference.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full rounded-2xl"
              />
              <figcaption className="mt-3 text-center text-xs" style={{ color: "rgba(233,246,248,.45)" }}>
                reference clip
              </figcaption>
            </figure>
          )}

          <figure
            className="flex flex-col rounded-3xl p-4"
            style={{ background: "#000", border: "1px solid rgba(255,255,255,.1)" }}
          >
            <div className="flex flex-1 items-center justify-center">
              <div className="aspect-square w-full max-w-[520px] p-10">
                <TalkingFace mode={mode} speed={speed} playing={playing} glow={glow} color={color} />
              </div>
            </div>
            <figcaption className="mt-3 text-center text-xs" style={{ color: "rgba(233,246,248,.45)" }}>
              rebuilt — {MODES.find((m) => m.id === mode)?.hint}
            </figcaption>
          </figure>
        </div>

        <div className="mt-8 flex flex-col gap-4 rounded-3xl p-5" style={{ background: "rgba(255,255,255,.03)" }}>
          <Control>
            {MODES.map((m) => (
              <Pill key={m.id} active={mode === m.id} onClick={() => setMode(m.id)} title={m.hint}>
                {m.label}
              </Pill>
            ))}
            <span className="mx-2 h-6 w-px" style={{ background: "rgba(255,255,255,.12)" }} />
            <Pill active={playing} onClick={() => setPlaying((p) => !p)}>
              {playing ? "Pause" : "Play"}
            </Pill>
            {SPEEDS.map((s) => (
              <Pill key={s} active={speed === s} onClick={() => setSpeed(s)}>
                {s}×
              </Pill>
            ))}
            <span className="mx-2 h-6 w-px" style={{ background: "rgba(255,255,255,.12)" }} />
            <Pill active={showRef} onClick={() => setShowRef((v) => !v)}>
              Reference
            </Pill>
          </Control>

          <Control>
            <label className="flex items-center gap-3 text-sm" style={{ color: "rgba(233,246,248,.7)" }}>
              Glow
              <input
                type="range"
                min="0"
                max="12"
                step="0.5"
                value={glow}
                onChange={(e) => setGlow(Number(e.target.value))}
              />
              <span className="w-8 tabular-nums">{glow}</span>
            </label>
            <span className="mx-2 h-6 w-px" style={{ background: "rgba(255,255,255,.12)" }} />
            {[
              ["#5fe9f5", "cyan (clip)"],
              ["#fdd07a", "amber (device)"],
              ["#b8c7b1", "sage"],
            ].map(([c, label]) => (
              <Pill key={c} active={color === c} onClick={() => setColor(c)} title={label}>
                <span
                  className="inline-block h-3 w-3 rounded-full align-middle"
                  style={{ background: c, boxShadow: `0 0 8px ${c}` }}
                />
                <span className="ml-2 align-middle">{label}</span>
              </Pill>
            ))}
          </Control>
        </div>
      </div>
    </main>
  );
}
